import { useState, useEffect } from 'react';
import { FileText, Check, Trash2, Loader2, Eye, ChevronDown } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/** Brouillons IA : relire puis publier (ou supprimer) les leçons générées. */
export default function AdminDrafts({ token, modules = [], reload }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try { setDrafts(await apiCall('/admin/drafts', 'GET', null, token)); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const modTitle = (id) => modules.find(m => m.id === id)?.title_fr || id;

  const publish = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      await apiCall(`/admin/lessons/${id}/publish`, 'PUT', { publish: true }, token);
      toast.success('Leçon publiée ✓');
      setDrafts(d => d.filter(x => x.id !== id));
      reload && reload();
    } catch (e) { toast.error(e.message); }
    setBusy(b => ({ ...b, [id]: false }));
  };
  const del = async (id) => {
    if (!window.confirm('Supprimer ce brouillon ?')) return;
    try {
      await apiCall(`/admin/lessons/${id}`, 'DELETE', null, token);
      setDrafts(d => d.filter(x => x.id !== id));
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="text-center py-10 text-white/60"><Loader2 className="animate-spin mx-auto" /></div>;

  const byModule = {};
  drafts.forEach(d => { (byModule[d.module_id] ||= []).push(d); });

  return (
    <div data-testid="admin-drafts">
      <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><FileText size={18} className="text-emerald-400" />Brouillons à relire ({drafts.length})</h3>
      <p className="text-sm text-white/60 mb-4">Leçons générées par l'IA, en attente de publication. Cliquez « Publier » pour les rendre visibles aux élèves.</p>

      {drafts.length === 0 && <div className="text-center text-white/40 py-8 text-sm">Aucun brouillon en attente.</div>}

      <div className="space-y-5">
        {Object.entries(byModule).map(([mid, items]) => (
          <div key={mid}>
            <div className="text-sm font-bold text-yellow-200 mb-2">{modTitle(mid)} — {items.length} brouillon(s)</div>
            <div className="space-y-2">
              {items.map(l => {
                const c = l.content || {};
                const isOpen = open === l.id;
                return (
                  <div key={l.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={`draft-${l.id}`}>
                    <div className="flex items-center justify-between p-3 gap-2">
                      <button onClick={() => setOpen(isOpen ? null : l.id)} className="flex-1 text-left flex items-center gap-2 min-w-0">
                        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        <span className="font-bold text-sm truncate">{l.title}</span>
                        <span className="text-xs text-white/40">{l.level}</span>
                      </button>
                      <button onClick={() => publish(l.id)} disabled={busy[l.id]} data-testid={`publish-draft-${l.id}`}
                        className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-1.5 px-3 rounded-lg text-xs flex items-center gap-1">
                        {busy[l.id] ? <Loader2 className="animate-spin" size={12} /> : <Check size={12} />}Publier
                      </button>
                      <button onClick={() => del(l.id)} className="text-red-300 hover:text-red-200 p-1" data-testid={`del-draft-${l.id}`}><Trash2 size={14} /></button>
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4 text-sm border-t border-white/5 pt-3 space-y-3">
                        <div>
                          <div className="text-xs uppercase text-emerald-300 mb-1">Vocabulaire ({c.step1_lecture?.items?.length || 0})</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(c.step1_lecture?.items || []).slice(0, 20).map((it, i) => (
                              <span key={i} className="bg-slate-900/60 rounded px-2 py-0.5 text-xs"><b>{it.src}</b> — {it.tgt}</span>
                            ))}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs text-white/60">
                          <div className="bg-slate-900/60 rounded p-2 text-center"><div className="font-bold text-white">{c.step2_exercises?.length || 0}</div>Exercices</div>
                          <div className="bg-slate-900/60 rounded p-2 text-center"><div className="font-bold text-white">{c.step3_minichat?.length || 0}</div>Mini-chat</div>
                          <div className="bg-slate-900/60 rounded p-2 text-center"><div className="font-bold text-white">{c.step4_audio?.length || 0}</div>Audio</div>
                        </div>
                        {(c.step4_audio || []).slice(0, 3).map((a, i) => (
                          <div key={i} className="text-xs text-white/60"><b className="text-white/80">{a.text}</b> — {a.translation}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
